import React, { useState, useEffect } from 'react';
import { Empty, Statistic, Col, DatePicker, Select } from 'antd';
import { usePageHeader } from '../../../layouts/AdminLayout';
import HeartLoader from '../../../components/shared/HeartLoader';
import { ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import Chart from 'react-apexcharts';
import {
  ChartCard,
  StatCard,
  StatsRow,
  PageContainer,
  selectStyles,
  rangePickerStyles,
  eventSelectStyles,
  valueColors
} from '../styled';
import VisitService from '../../../services/VisitService';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const WaitTimesFiltersContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 24px;

  /* Override global 48px to 40px for all controls inside filters */
  .ant-btn,
  .ant-btn-default,
  .ant-btn-primary {
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    font-size: 14px !important;
    padding: 0 16px !important;
  }

  .ant-select,
  .ant-select-single,
  .ant-select-lg,
  .ant-select-single.ant-select-lg {
    height: 40px !important;
  }

  .ant-select .ant-select-selector,
  .ant-select-lg .ant-select-selector,
  .ant-select-single .ant-select-selector,
  .ant-select-single.ant-select-lg .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
    font-size: 14px !important;
  }

  .ant-select .ant-select-selection-placeholder,
  .ant-select .ant-select-selection-item,
  .ant-select-lg .ant-select-selection-placeholder,
  .ant-select-lg .ant-select-selection-item {
    line-height: 38px !important;
    font-size: 14px !important;
  }

  .ant-picker,
  .ant-picker-large {
    height: 40px !important;
    min-height: 40px !important;
    font-size: 14px !important;
  }

  .ant-picker .ant-picker-input input,
  .ant-picker-large .ant-picker-input input {
    font-size: 14px !important;
  }

  .ant-input,
  .ant-input-lg {
    height: 40px !important;
    min-height: 40px !important;
    font-size: 14px !important;
  }
`;

const DetailedAnalyticsCard = styled(ChartCard)`
  margin-top: 24px;
`;

const loadingContainerStyles = {
  padding: '80px'
};

const WaitTimes = () => {
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [quickRange, setQuickRange] = useState('30days');
  const [eventFilter, setEventFilter] = useState('all');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState({
    checkInToIntake: true,
    intakeToBus: true,
    screeningTime: true
  });

  useEffect(() => {
    setPageHeader({ title: 'Wait Times Analytics', count: null, countIcon: null });
  }, [setPageHeader]);

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, eventFilter]);

  const loadEvents = async () => {
    try {
      const response = await VisitService.getAnalyticsEvents();
      setEvents(response || []);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await VisitService.getWaitTimeAnalytics({
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
        eventId: eventFilter
      });
      
      setAnalyticsData(response);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setQuickRange('custom');
    }
  };

  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    
    const now = dayjs();
    let newRange;
    
    switch (value) {
      case '7days':
        newRange = [now.subtract(7, 'days'), now];
        break;
      case '14days':
        newRange = [now.subtract(14, 'days'), now];
        break;
      case '30days':
        newRange = [now.subtract(30, 'days'), now];
        break;
      case '3months':
        newRange = [now.subtract(3, 'months'), now];
        break;
      case 'Q1':
        newRange = [dayjs().startOf('year'), dayjs().startOf('year').month(2).endOf('month')];
        break;
      case 'Q2':
        newRange = [dayjs().startOf('year').month(3), dayjs().startOf('year').month(5).endOf('month')];
        break;
      case 'Q3':
        newRange = [dayjs().startOf('year').month(6), dayjs().startOf('year').month(8).endOf('month')];
        break;
      case 'Q4':
        newRange = [dayjs().startOf('year').month(9), dayjs().startOf('year').month(11).endOf('month')];
        break;
      default:
        return;
    }
    
    setDateRange(newRange);
  };

  const formatTime = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      if (mins === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${mins}m`;
    }
    return `${minutes.toFixed(1)} min`;
  };

  const formatTooltipTime = (minutes) => {
    if (minutes === 0) return 'No data';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      if (mins === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      }
      return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} min`;
    }
    return `${minutes.toFixed(1)} min`;
  };

  const handleMetricClick = (metric) => {
    const allSelected = Object.values(selectedMetrics).every(v => v);
    const noneSelected = Object.values(selectedMetrics).every(v => !v);
    
    if (allSelected) {
      setSelectedMetrics({
        checkInToIntake: metric === 'checkInToIntake',
        intakeToBus: metric === 'intakeToBus',
        screeningTime: metric === 'screeningTime'
      });
    } else if (noneSelected) {
      setSelectedMetrics({
        checkInToIntake: true,
        intakeToBus: true,
        screeningTime: true
      });
    } else {
      setSelectedMetrics(prev => ({
        ...prev,
        [metric]: !prev[metric]
      }));
    }
  };

  if (loading && !analyticsData) {
    return (
      <PageContainer>
        <HeartLoader size="48px" fullHeight />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <WaitTimesFiltersContainer className="filters-animate-in">
        <Select
          value={quickRange}
          onChange={handleQuickRangeChange}
          style={selectStyles}
          size="large"
        >
          <Option value="7days">Last 7 days</Option>
          <Option value="14days">Last 14 days</Option>
          <Option value="30days">Last 30 days</Option>
          <Option value="3months">Last 3 months</Option>
          <Option value="Q1">Q1 (Jan-Mar)</Option>
          <Option value="Q2">Q2 (Apr-Jun)</Option>
          <Option value="Q3">Q3 (Jul-Sep)</Option>
          <Option value="Q4">Q4 (Oct-Dec)</Option>
          <Option value="custom">Custom</Option>
        </Select>

        <RangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          format="MM/DD/YY"
          style={rangePickerStyles}
          size="large"
          allowClear={false}
        />

        <Select
          value={eventFilter}
          onChange={setEventFilter}
          style={eventSelectStyles}
          size="large"
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
          }
          placeholder="Select an event"
        >
          <Option value="all">All Events</Option>
          {events.slice(0, 7).map(event => (
            <Option key={event.bookingId} value={event.bookingId}>
              {event.title} - {event.location}
            </Option>
          ))}
        </Select>
      </WaitTimesFiltersContainer>

      {!analyticsData ? (
        <Empty 
          description="No data available for the selected period"
          style={loadingContainerStyles}
        />
      ) : (
        <>
          <StatsRow gutter={[16, 16]} className="stats-animate-in">
            <Col xs={24} sm={12} lg={6}>
              <StatCard 
                onClick={() => handleMetricClick('checkInToIntake')}
                style={{ 
                  cursor: 'pointer',
                  opacity: selectedMetrics.checkInToIntake ? 1 : 0.5,
                  border: selectedMetrics.checkInToIntake ? '2px solid #3f8600' : '1px solid #d9d9d9',
                  transition: 'all 0.3s ease'
                }}
              >
                <Statistic
                  title="Avg Check-In to Intake"
                  value={formatTime(analyticsData.avgCheckInToIntake)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard 
                onClick={() => handleMetricClick('intakeToBus')}
                style={{ 
                  cursor: 'pointer',
                  opacity: selectedMetrics.intakeToBus ? 1 : 0.5,
                  border: selectedMetrics.intakeToBus ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  transition: 'all 0.3s ease'
                }}
              >
                <Statistic
                  title="Avg Intake to Screening"
                  value={formatTime(analyticsData.avgIntakeToBus)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard 
                onClick={() => handleMetricClick('screeningTime')}
                style={{ 
                  cursor: 'pointer',
                  opacity: selectedMetrics.screeningTime ? 1 : 0.5,
                  border: selectedMetrics.screeningTime ? '2px solid #cf1322' : '1px solid #d9d9d9',
                  transition: 'all 0.3s ease'
                }}
              >
                <Statistic
                  title="Avg Screening Time"
                  value={formatTime(analyticsData.avgScreeningTime)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard>
                <Statistic
                  title="Total Members"
                  value={analyticsData.totalMembers}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: valueColors.purple }}
                />
              </StatCard>
            </Col>
          </StatsRow>

          <DetailedAnalyticsCard title="Wait Time Trends Over Time" className="chart-animate-in">
            {analyticsData?.timeSeriesData && analyticsData.timeSeriesData.length > 0 ? (
              (() => {
                const categories = analyticsData.timeSeriesData.map(item => item.date);
                const series = [];
                
                if (selectedMetrics.checkInToIntake) {
                  series.push({
                    name: 'Form to Intake',
                    data: analyticsData.timeSeriesData.map(item => item.avgCheckInToIntake || 0)
                  });
                }
                if (selectedMetrics.intakeToBus) {
                  series.push({
                    name: 'Intake to Screening',
                    data: analyticsData.timeSeriesData.map(item => item.avgIntakeToBus || 0)
                  });
                }
                if (selectedMetrics.screeningTime) {
                  series.push({
                    name: 'Screening Time',
                    data: analyticsData.timeSeriesData.map(item => item.avgScreeningTime || 0)
                  });
                }

                const colors = [];
                if (selectedMetrics.checkInToIntake) colors.push('#3f8600');
                if (selectedMetrics.intakeToBus) colors.push('#1890ff');
                if (selectedMetrics.screeningTime) colors.push('#cf1322');

                const options = {
                  chart: {
                    type: 'line',
                    toolbar: { show: false },
                    zoom: { enabled: false }
                  },
                  colors: colors,
                  stroke: {
                    curve: 'smooth',
                    width: 2
                  },
                  markers: {
                    size: 4,
                    hover: { size: 7 }
                  },
                  xaxis: {
                    categories: categories,
                    labels: {
                      rotate: -45,
                      rotateAlways: false,
                      style: { fontSize: '11px' }
                    },
                    tickPlacement: 'on'
                  },
                  yaxis: {
                    title: { text: 'Minutes' },
                    labels: {
                      formatter: (val) => val?.toFixed(1) || '0'
                    }
                  },
                  legend: {
                    position: 'top',
                    horizontalAlign: 'center'
                  },
                  tooltip: {
                    shared: true,
                    y: {
                      formatter: (val) => formatTooltipTime(val || 0)
                    }
                  },
                  grid: {
                    borderColor: '#f0f0f0',
                    strokeDashArray: 4
                  }
                };

                return (
                  <Chart
                    options={options}
                    series={series}
                    type="line"
                    height={400}
                  />
                );
              })()
            ) : (
              <Empty description="No data available for the selected time period" />
            )}
          </DetailedAnalyticsCard>
        </>
      )}
    </PageContainer>
  );
};

export default WaitTimes;
